import React, { useEffect, useState } from 'react';
import { getCardBarcode, getRemoteCardDetails } from 'common/api/wallet/cards';
import { Card } from 'common/api/e-comm/models/Card';
import { BarCode } from 'components/custom-icons';
import { ECommBrand } from 'common/api/e-comm/models/ECommBrand';
import { getCardName } from 'common/api/utils/getCardName';
import { useRootSelector } from 'common/features/featuresReducer';
import { useDispatch } from 'react-redux';
import { upsertBarcodes } from 'common/features/store/duck/wallet/duck';
import { X } from 'react-feather';
import { useSpring, animated } from 'react-spring';
import { createStyles } from 'utils/createStyle';
import { ProductType } from 'common/api/e-comm/models/Product';
import { RemoteCardDetails } from 'common/api/e-comm/models/RemoteCardDetails';
import { useAlert } from 'modules/alerts';

interface IMobileBarCodeViewer {
    brand?: ECommBrand;
    card?: Card | null;
    shouldHideCardBarCode?: boolean;
}

const styles = createStyles({
    barcode: [
        'mx-auto',
        {
            width: 315,
        },
    ],
    cardname: ['', { height: 75, width: 120 }],
});

export const MobileBarCodeViewer = (props: IMobileBarCodeViewer) => {
    const [showBarcodeViewer, setShowBarcodeViewer] = useState<boolean>(false);
    const [remoteCardDetails, setRemoteCardDetails] = useState<
        RemoteCardDetails | null | undefined
    >(null);
    const alert = useAlert();
    const { card, shouldHideCardBarCode } = props;

    const openBarcodeViewer = () => {
        setShowBarcodeViewer((state: boolean) => !state);
    };

    useEffect(() => {
        if (card?.settings?.useRemoteCardStorage) {
            const fetchRemoteCard = async () => {
                try {
                    const { data } = await getRemoteCardDetails(card.id);
                    setRemoteCardDetails(data);
                } catch (e) {
                    alert({ message: 'Unable to fetch card details.' });
                }
            };
            fetchRemoteCard();
        }
    }, [card, alert]);

    // CASE: we __only__ want to render the barcode viewer for eGift cards
    if ((props.card && props.card.type !== ProductType.EGiftCard) || shouldHideCardBarCode) {
        return null;
    }

    return (
        <div className="block sm:hidden">
            <div
                className="cursor-pointer text-brand border rounded-full border-brand p-3"
                onClick={openBarcodeViewer}
            >
                <BarCode height="1rem" className="inline-block m-auto" />
            </div>
            <p className="text-xs text-brand">Use Card</p>
            {!remoteCardDetails && (
                <BarCodeViewer
                    onClose={setShowBarcodeViewer}
                    isOpen={showBarcodeViewer}
                    {...props}
                />
            )}
            {remoteCardDetails && (
                <RemoteCardViewer
                    onClose={setShowBarcodeViewer}
                    isOpen={showBarcodeViewer}
                    remoteCardDetails={remoteCardDetails}
                    {...props}
                />
            )}
        </div>
    );
};

interface IBarCodeViewer extends Pick<IMobileBarCodeViewer, 'card' | 'brand'> {
    isOpen: boolean;
    onClose: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ISchModal {
    isOpen: boolean;
    onClose: () => void;
}

const SchModal = ({ isOpen, onClose, children }: React.PropsWithChildren<ISchModal>) => {
    const styles = useSpring({
        opacity: isOpen ? 1 : 0,
    });

    if (!isOpen) {
        return null;
    }

    return (
        <div className="z-50 fixed top-0 left-0 bg-white shadow-md w-full h-full">
            <div className="mx-auto p-5 w-full">
                <animated.div style={styles}>
                    <div className="flex justify-end">
                        <button onClick={onClose}>
                            <X size={28} />
                        </button>
                    </div>
                    <div className="flex flex-col items-center">{children}</div>
                </animated.div>
            </div>
        </div>
    );
};

const BarCodeViewer = (props: IBarCodeViewer) => {
    const onClose_ = () => {
        props.onClose(false);
    };

    if (!props.card) {
        return null;
    }

    return (
        <SchModal isOpen={props.isOpen} onClose={onClose_}>
            <div className="w-full flex flex-col text-center gap-10">
                <CardImage brand={props.brand} />
                <CardName card={props.card} brand={props.brand} />
                <CardBarcode card={props.card} />
                <ExtraInfo card={props.card} />
            </div>
        </SchModal>
    );
};

interface RemoteCardViewerProps extends IBarCodeViewer {
    remoteCardDetails: RemoteCardDetails;
}

const RemoteCardViewer = ({
    card,
    brand,
    isOpen,
    onClose,
    remoteCardDetails,
}: RemoteCardViewerProps) => {
    const onClose_ = () => onClose(false);

    return (
        <SchModal isOpen={isOpen} onClose={onClose_}>
            <div className="w-full flex flex-col text-center gap-10">
                <CardImage brand={brand} />
                {card && <CardName card={card} brand={brand} />}
                <div className="flex flex-col gap-3">
                    <div>
                        <p className="text-brand-dark font-semibold">CardNumber</p>
                        <p data-recording-sensitive>{remoteCardDetails.cardNumber}</p>
                    </div>
                    <div>
                        <p className="text-brand-dark font-semibold">Pin</p>
                        <p data-recording-sensitive>{remoteCardDetails.pin}</p>
                    </div>
                </div>
            </div>
        </SchModal>
    );
};

const CardImage = ({ brand }: { brand?: ECommBrand }) => (
    <div>
        {brand ? (
            <img className="mx-auto border-1" src={brand?.imageUrl} />
        ) : (
            <div className={styles.cardname}>&nbsp;</div>
        )}
    </div>
);

const CardName = ({ card, brand }: { card: Card; brand?: ECommBrand }) => (
    <p className="text-5xl text-brand-dark font-semibold">{getCardName(card, brand)}</p>
);

const shouldUseBarcode = (card: Card): boolean => {
    return Boolean(
        card.settings &&
            card.settings.cardNumber &&
            card.settings.cardNumber.isBarcodeVisible &&
            card.settings.barcodeType != null
    );
};

const CardBarcode = ({ card }: { card: Card }) => {
    const barcode = useRootSelector((s) => s.store.wallet.barcodesById[card.id]);
    const dispatch = useDispatch<any>();

    useEffect(() => {
        const fetchBarCode = async () => {
            const response = await getCardBarcode(card.id);
            if (response.data) {
                dispatch(upsertBarcodes({ [card.id]: response.data }));
            }
        };

        if (card && shouldUseBarcode(card) && !barcode) {
            fetchBarCode();
        }
    }, [barcode, card, dispatch]);

    if (!barcode) {
        return null;
    }

    return (
        <div data-sl="exclude" className="mx-auto w-full">
            <img className={styles.barcode} src={barcode} />
        </div>
    );
};

const ExtraInfo = ({ card }: { card: Card }) => {
    return (
        <div className="flex flex-col gap-4">
            <CardNumberRenderer card={card} />
            <PinRenderer card={card} />
            <AdditionalLabelRenderer card={card} />
        </div>
    );
};

const CardNumberRenderer = ({ card }: { card: Card }) => {
    // NOTE: do not render anything if cardNumber isn't visible
    if (!(card?.settings?.cardNumber?.isVisible || card?.formattedCardNumber)) {
        return null;
    }

    return (
        <div>
            <p className="text-brand-dark font-semibold">
                {card?.settings?.cardNumber?.label || 'Card Number'}
            </p>
            <p data-recording-sensitive>{card.formattedCardNumber}</p>
        </div>
    );
};

const PinRenderer = ({ card }: { card: Card }) => {
    if (!card?.settings?.pin || !card?.settings?.pin?.isVisible || !card?.pin) {
        return null;
    }

    return (
        <div>
            <p className="text-brand-dark font-semibold">{card?.settings?.pin?.label || 'PIN'}</p>
            <p data-recording-sensitive>{card.pin}</p>
        </div>
    );
};

const AdditionalLabelRenderer = ({ card }: { card: Card }) => {
    if (!card?.settings?.extraField?.isVisible || !card.extraFieldValue) {
        return null;
    }

    return (
        <div>
            <p className="text-brand-dark font-semibold">
                {card?.settings?.extraField?.label || 'Event Number'}
            </p>
            <p>{card.extraFieldValue}</p>
        </div>
    );
};
